import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Button,
  Empty,
  Tag,
  Form,
  Input,
  message,
  Modal,
  FormInstance,
} from "antd";
import { UserOutlined, MailOutlined } from "@ant-design/icons";
import { getIconComponent } from "../../../../../Utils/iconUtils";
import { supabase } from "../../../../../config/supabaseClient";
import "./MyStoreProducts.css";
import { ExtendedProduct } from "../../../../Order/OrderSuccess";
import BookingCalendar from "../../../../../components/Calendar/BookingCalendar";
import dayjs from "dayjs";
import NoPostMsg from "../../../../../components/NoPostMsg/NoPostMsg";
import { useMutation } from "react-query";
import {
  getApiRequestHeaders,
  isStripeTestModeSync,
} from "../../../../../Utils/StripeTestMode";
import { StaticVariables } from "../../../../../Utils/StaticVariables";
import { BannerSkeleton } from "../../../../../components/StayUpToDateComponent/BannerSkeleton";
import {
  getShopParityButtonStyle,
  lightenColor,
} from "../../../../../Utils/StayUpToDate/RenderComponent";
import { useDesign } from "../../../../../context/DesignContext";
import StoreProductCard from "../../../Store/components/StoreProductCard";
import StoreProductCardLinkSkeleton from "../../../Store/components/StoreProductCardLinkSkeleton";
import type { ProductDisplayType } from "../../../Store/types/productCreation";
import { getStoreProductCardThemeStyle } from "../../../Store/utils/designPreviewTemplate";
import { openProductCheckoutInNewTab } from "../../../Store/utils/productRecordHelpers";
import { useWebsiteTemplateLoaders } from "../../../MasterTemplateEditor/TemplateFeedDesignBoundary";
import { readWebsiteStoreTokens } from "../../../MasterTemplateEditor/digitalProductBlock";
import { isHexDark } from "../../../../../Utils/HelperFunction/HelperFunction";
import { darkenColor } from "../../LandingPageComponents/LinksComponent/Components/hoverUtils";

export interface Product {
  id: string;
  metadata: {
    product_details: {
      title: string;
      subtitle?: string;
      description: string;
      buttonText?: string;
      displayType?: ProductDisplayType;
      thumbnailUrl: string;
      bottomTitle: string;
    };
    delivery?: {
      type?: "file" | "redirect";
      content?:
      | Array<{
        url: string;
        name: string;
        size: number;
        type: string;
      }>
      | string;
    };
    collect_info?: {
      required_fields?: Array<{
        type: string;
        label: string;
        required: boolean;
        icon?: string;
      }>;
    };
    availability?: {
      timeZone: string;
      duration: number;
      preventBookingWithinHours: number;
      maxAttendees: number;
      breakBefore: number;
      breakAfter: number;
      bookWithinDays: number;
      meetingPlatform: string;
      meetingLink?: string;
      schedule: Array<{
        day: string;
        slots: Array<{
          from: string;
          to: string;
        }>;
      }>;
    };
  };
  status: "active" | "draft" | "archived";
  product_type: string;
  price: number;
  name: string;
  category: string;
  stripe_product_id?: string;
  stripe_price_id?: string;
  user_id: string;
}

interface MyStoreProductsProps {
  component: {
    id: string;
    type: string;
    title: string;
    description: string;
    button_text: string;
    user_id?: string;
    post_ids?: string;
    name?: string;
  };
  brand_color?: string;
  from?: string;
  user_id?: string;
  refetch?: () => void; // Added refetch prop
  username?: string; // Added username prop
  isDeleting?: boolean;
  setIsDeleting?: (value: boolean) => void;
  isLoading?: boolean;
  setIsLoading?: (value: boolean) => void;
  designTokens?: any;
  isMobileView?: boolean;
}

interface FormValues {
  name: string;
  email: string;
  [key: string]: any;
}

const ProductForm: React.FC<{
  product: Product;
  from?: string;
  onSubmit: (values: FormValues, form: FormInstance, product: Product) => void;
  brand_color?: string;
  designTokens?: any;
  isMobileView?: boolean;
}> = ({ product, from, onSubmit, brand_color, designTokens, isMobileView }) => {
  const [form] = Form.useForm();
  const isFollowerShopParity = from === "frontend";
  const designCtx = useDesign();
  const mergedTokensForShop = useMemo(
    () => ({
      templateName: designTokens?.templateName ?? designCtx.templateName,
      secondaryColor: designTokens?.secondaryColor ?? designCtx.secondaryColor,
      primaryColor: designTokens?.primaryColor ?? designCtx.primaryColor,
      tertiaryColor: designTokens?.tertiaryColor ?? designCtx.tertiaryColor,
      primaryFontFamily: designTokens?.primaryFontFamily ?? designCtx?.primaryFont,
      secondaryFontFamily: designTokens?.secondaryFontFamily ?? designCtx?.secondaryFont,
      forthColor: designTokens?.forthColor ?? designCtx?.forthColor,
    }),
    [
      designCtx?.forthColor,
      designCtx.primaryColor,
      designCtx.secondaryColor,
      designCtx.templateName,
      designCtx.tertiaryColor,
      designTokens?.primaryColor,
      designTokens?.secondaryColor,
      designTokens?.templateName,
      designTokens?.tertiaryColor,
      designTokens?.primaryFontFamily,
      designTokens?.secondaryFontFamily,
      designTokens?.forthColor,
    ],
  );
  const shopParity = isFollowerShopParity
    ? getShopParityButtonStyle({
        designTokens: mergedTokensForShop,
        brand_color,
        // Match ProductShopButton / .shop-link fill (forthColor).
        contextButtonBackground: mergedTokensForShop.forthColor,
      })
    : null;
  const shopParityButtonStyle: React.CSSProperties | undefined = shopParity
    ? {
        ...shopParity.structural,
        width: "100%",
        ["--follower-cta-bg" as string]: shopParity.backgroundColor,
        ["--follower-cta-fg" as string]: shopParity.color,
        ["--hover-bg" as string]: shopParity.hoverBg,
        border: "none",
        boxShadow: "none",
        borderRadius: 6,
        ...(designTokens?.fontFamily ? { fontFamily: designTokens.fontFamily } : {}),
      }
    : undefined;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    date: dayjs.Dayjs;
    slot: { from: string; to: string };
  } | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);

  const handleSubmit = async (values: FormValues) => {
    if (from === "landing-page") return;

    if (product.name === "consultation") {
      if (!selectedTimeSlot) {
        message.error("Please select a time slot");
        return;
      }
      values.selectedTimeSlot = selectedTimeSlot;
    }

    if (product.name === "digital_product" || product.name === "freebie") {
      openProductCheckoutInNewTab(product.id, { values, designTokens });
      return;
    }

    // Handle other product types
    setIsSubmitting(true);
    try {
      await onSubmit(values, form, product);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTimeSlotSelect = (
    date: dayjs.Dayjs,
    slot: { from: string; to: string }
  ) => {
    setSelectedTimeSlot({ date, slot });
    setShowCalendar(false); // Hide calendar after slot selection
  };

  const renderConsultationForm = () => {
    if (!product.metadata?.availability) return null;

    if (showCalendar) {
      return (
        <div className="consultation-calendar">
          <Button
            type="link"
            onClick={() => setShowCalendar(false)}
            style={{ marginBottom: "16px", fontFamily: mergedTokensForShop?.secondaryFontFamily }}
          >
            ← Back to Form
          </Button>
          <BookingCalendar
            timeZone={product.metadata.availability.timeZone}
            schedule={product.metadata.availability.schedule || []}
            onTimeSlotSelect={handleTimeSlotSelect}
          />
        </div>
      );
    }

    return (
      <div className="consultation-form" style={{fontFamily: mergedTokensForShop?.secondaryFontFamily}}>
        {/* Show selected time slot if any */}
        {selectedTimeSlot && (
          <div
            className="selected-slot-info"
            style={{
              marginBottom: "16px",
              padding: "12px",
              background: "#f0f2f5",
              borderRadius: "6px",
            }}
          >
            <h4>Selected Time:</h4>
            <p>{selectedTimeSlot.date.format("MMMM D, YYYY")}</p>
            <p>
              {selectedTimeSlot.slot.from} - {selectedTimeSlot.slot.to}
            </p>
            <Button type="link" onClick={() => setShowCalendar(true)}>
              Change Time
            </Button>
          </div>
        )}

        {/* Default fields */}
        <Form.Item
          name="name"
          rules={[{ required: true, message: "Please enter name" }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="Enter name"
            disabled={from === "landing-page"}
            style={{
              color: designTokens?.tertiaryColor || "#000",
              fontFamily: mergedTokensForShop?.secondaryFontFamily,
              backgroundColor: mergedTokensForShop?.forthColor,
            }}
          />
        </Form.Item>

        <Form.Item
          name="email"
          rules={[
            { required: true, message: "Please enter email" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder="Enter email"
            type="email"
            disabled={from === "landing-page"}
            style={{
              color: designTokens?.tertiaryColor || "#000",
              fontFamily: mergedTokensForShop?.secondaryFontFamily,
              backgroundColor: mergedTokensForShop?.forthColor,
            }}
          />
        </Form.Item>

        {/* Custom fields with improved styling */}
        {product.metadata?.collect_info?.required_fields?.map(
          (field, index) => {
            const isPhoneNumberField =
              field.icon === "PhoneOutlined" || field.type === "tel";
            const rules = [
              {
                required: field.required,
                message: `Please enter your ${field.label}`,
              },
              ...(isPhoneNumberField
                ? [{ pattern: /^\d+$/, message: "Please enter digits only" }]
                : []),
            ];
            return (
              field.label.toLowerCase() !== "name" &&
              field.label.toLowerCase() !== "email" && (
                <Form.Item
                  key={index}
                  name={field.label.toLowerCase()}
                  rules={rules}
                >
                  <Input
                    prefix={
                      field.icon ? (
                        React.createElement(getIconComponent(field.icon))
                      ) : field.type === "email" ? (
                        <MailOutlined />
                      ) : (
                        <UserOutlined />
                      )
                    }
                    placeholder={`Enter your ${field.label}`}
                    type={field.type}
                    disabled={from === "landing-page"}
                    inputMode={isPhoneNumberField ? "numeric" : undefined}
                    pattern={isPhoneNumberField ? "[0-9]*" : undefined}
                    style={{
                      color: designTokens?.tertiaryColor || "#000",
                      fontFamily: mergedTokensForShop?.secondaryFontFamily,
                      backgroundColor: mergedTokensForShop?.forthColor,
                    }}
                  />
                </Form.Item>
              )
            );
          }
        )}

        {/* Choose Date Button */}
        <Button
          type="primary"
          onClick={() => setShowCalendar(true)}
          style={{ width: "100%", marginBottom: "16px" }}
          disabled={from === "landing-page"}
        >
          {selectedTimeSlot ? "Change Date & Time" : "Choose Date & Time"}
        </Button>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            className={`my-store-product-btn ${
              isFollowerShopParity
                ? "follower-shop-parity-btn custom-button-with-hover"
                : designTokens?.primaryColor || brand_color
                ? "custom-button-with-hover"
                : ""
            }`}
            style={
              shopParityButtonStyle ?? {
                width: "100%",
                backgroundColor: designTokens?.forthColor
                  ? designTokens?.forthColor
                  : brand_color,
                color: "#fff",
              ...(designTokens?.secondaryFontFamily ? { fontFamily: designTokens.secondaryFontFamily } : {}),
              ...((designTokens?.forthColor || brand_color)
                ? { ["--hover-bg" as any]:designTokens?.forthColor ? (isHexDark(designTokens?.forthColor) ? lightenColor(designTokens?.forthColor, 20) : darkenColor(designTokens?.forthColor, 10)) : lightenColor(brand_color || "#000", 20) }
                : {}),
              }
            }
            disabled={
              from === "landing-page" || isSubmitting || !selectedTimeSlot
            }
            loading={isSubmitting}
          >
            {isSubmitting
              ? "Processing..."
              : product.metadata?.product_details?.buttonText ||
              "Book Consultation"}
          </Button>
        </Form.Item>
      </div>
    );
  };

  if (product.name === "consultation") {
    return renderConsultationForm();
  }

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      className="product-form"
      layout="vertical"
      style={designTokens?.secondaryFontFamily ? { fontFamily: mergedTokensForShop?.secondaryFontFamily } : undefined}
    >
      <Form.Item
        name="name"
        rules={[{ required: true, message: "Please enter name" }]}
      >
        <Input
          prefix={<UserOutlined />}
          placeholder="Enter name"
          disabled={from === "landing-page"}
          style={{
              color: designTokens?.tertiaryColor || "#000",
              fontFamily: mergedTokensForShop?.secondaryFontFamily,
              backgroundColor: mergedTokensForShop?.forthColor,
            }}
        />
      </Form.Item>

      <Form.Item
        name="email"
        rules={[
          { required: true, message: "Please enter email" },
          { type: "email", message: "Please enter a valid email" },
        ]}
      >
        <Input
          prefix={<MailOutlined />}
          placeholder="Enter email"
          type="email"
          disabled={from === "landing-page"}
          style={{
              color: designTokens?.tertiaryColor || "#000",
              fontFamily: mergedTokensForShop?.secondaryFontFamily,
              backgroundColor: mergedTokensForShop?.forthColor,
            }}
        />
      </Form.Item>

      {/* Custom fields with improved styling */}
      {product.metadata?.collect_info?.required_fields?.map((field, index) => {
        const isPhoneNumberField =
          field.icon === "PhoneOutlined" || field.type === "tel";
        const rules = [
          {
            required: field.required,
            message: `Please enter ${field.label}`,
          },
          ...(isPhoneNumberField
            ? [{ pattern: /^\d+$/, message: "Please enter digits only" }]
            : []),
        ];
        return (
          field.label.toLowerCase() !== "name" &&
          field.label.toLowerCase() !== "email" && (
            <Form.Item
              key={index}
              name={field.label.toLowerCase()}
              rules={rules}
            >
              <Input
                prefix={
                  field.icon ? (
                    React.createElement(getIconComponent(field.icon))
                  ) : field.type === "email" ? (
                    <MailOutlined />
                  ) : (
                    <UserOutlined />
                  )
                }
                placeholder={`Enter ${field.label}`}
                type={field.type}
                disabled={from === "landing-page"}
                inputMode={isPhoneNumberField ? "numeric" : undefined}
                pattern={isPhoneNumberField ? "[0-9]*" : undefined}
                style={{
              color: designTokens?.tertiaryColor || "#000",
              fontFamily: mergedTokensForShop?.secondaryFontFamily,
              backgroundColor: mergedTokensForShop?.forthColor,
            }}
              />
            </Form.Item>
          )
        );
      })}

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          disabled={from === "landing-page"}
          className={`my-store-product-btn ${from === "landing-page" ? "disabled-btn w-100" : "w-100"} ${
            isFollowerShopParity
              ? "follower-shop-parity-btn custom-button-with-hover"
              : designTokens?.secondaryColor || brand_color
              ? "custom-button-with-hover"
              : ""
          }`}
          style={

            shopParityButtonStyle ?? {
              backgroundColor: designTokens?.forthColor
                ? designTokens?.forthColor
                : brand_color,
              color: "#fff",
              ...(designTokens?.secondaryFontFamily ? { fontFamily: designTokens.secondaryFontFamily } : {}),
              ...((designTokens?.forthColor || brand_color)
                ? { ["--hover-bg" as any]:designTokens?.forthColor ? (isHexDark(designTokens?.forthColor) ? lightenColor(designTokens?.forthColor, 20) : darkenColor(designTokens?.forthColor, 10)) : lightenColor(brand_color || "#000", 20) }
                : {}),
              borderRadius: 6,
            }
          }
          loading={isSubmitting}
        >
          {isSubmitting
            ? "Processing..."
            : product.metadata?.product_details?.buttonText ||
            "SUBMIT & DOWNLOAD"}
        </Button>
      </Form.Item>
    </Form>
  );
};

const ProductSkeleton = () => (
  // <div className="store-product-skeleton">
  <>
    <div className="store-skeleton-header">
      <div className="store-skeleton-title" />
      <div className="store-skeleton-tags">
        <div className="store-skeleton-tag" />
        <div className="store-skeleton-tag" />
      </div>
    </div>

    <div className="store-skeleton-container">
      <div className="store-skeleton-image" />
      <div className="store-skeleton-form">
        <div className="store-skeleton-input" />
        <div className="store-skeleton-input" />
        <div className="store-skeleton-button" />
      </div>
    </div>
  </>
  // </div>
);

const renderStoreCardLoader = ({
  displayType,
  isMobileView,
  designTokens,
  useDesignColors,
}: {
  displayType: ProductDisplayType;
  isMobileView: boolean;
  designTokens?: any;
  useDesignColors: boolean;
}) => {
  if (displayType === "full_view") {
    return (
      <BannerSkeleton
        designTokens={designTokens}
        forceNeutral={!useDesignColors}
      />
    );
  }

  return (
    <div className="store-product-card-shell frontend-product-wrapper">
      <StoreProductCardLinkSkeleton
        isMobileView={isMobileView}
        designTokens={useDesignColors ? designTokens : undefined}
      />
    </div>
  );
};

const MyStoreProducts: React.FC<MyStoreProductsProps> = ({
  component,
  brand_color,
  from,
  user_id,
  refetch,
  username,
  isDeleting,
  setIsDeleting,
  isLoading,
  setIsLoading,
  designTokens,
  isMobileView = false,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaderDisplayType, setLoaderDisplayType] = useState<ProductDisplayType>(
    () => {
      if (typeof window === "undefined" || !component?.post_ids) return "link";
      try {
        const cached = sessionStorage.getItem(
          `store-product-display-${component.post_ids}`
        );
        return cached === "full_view" ? "full_view" : "link";
      } catch {
        return "link";
      }
    }
  );
  const designCtx = useDesign();

  const mergedDesignTokens = useMemo(
    () => ({
      templateName: designTokens?.templateName ?? designCtx.templateName,
      primaryColor: designTokens?.primaryColor ?? designCtx.primaryColor,
      secondaryColor: designTokens?.secondaryColor ?? designCtx.secondaryColor,
      tertiaryColor: designTokens?.tertiaryColor ?? designCtx.tertiaryColor,
      forthColor: designTokens?.forthColor ?? designCtx.forthColor,
      primaryFontFamily:
        designTokens?.primaryFontFamily || designCtx.primaryFont,
      secondaryFontFamily:
        designTokens?.secondaryFontFamily || designCtx.secondaryFont,
      fontFamily: designTokens?.fontFamily || designCtx.fontFamily,
    }),
    [
      designCtx.fontFamily,
      designCtx.forthColor,
      designCtx.primaryColor,
      designCtx.primaryFont,
      designCtx.secondaryColor,
      designCtx.secondaryFont,
      designCtx.templateName,
      designCtx.tertiaryColor,
      designTokens?.fontFamily,
      designTokens?.forthColor,
      designTokens?.primaryColor,
      designTokens?.primaryFontFamily,
      designTokens?.secondaryFontFamily,
      designTokens?.secondaryColor,
      designTokens?.templateName,
      designTokens?.tertiaryColor,
    ]
  );

  const websiteSurface = useWebsiteTemplateLoaders();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [websiteTokens, setWebsiteTokens] = useState<ReturnType<
    typeof readWebsiteStoreTokens
  >>(null);

  useEffect(() => {
    if (!websiteSurface) {
      setWebsiteTokens(null);
      return;
    }
    // After paint so parent theme variables are already on the canvas.
    setWebsiteTokens(readWebsiteStoreTokens(surfaceRef.current));
  }, [websiteSurface, loading, products.length]);

  const cardTokens = useMemo(() => {
    if (!websiteTokens) return mergedDesignTokens;
    return { ...mergedDesignTokens, ...websiteTokens };
  }, [mergedDesignTokens, websiteTokens]);

  const storeCardThemeStyle = useMemo(() => {
    if (from !== "frontend" && !websiteTokens) return undefined;
    return getStoreProductCardThemeStyle(cardTokens, brand_color);
  }, [brand_color, cardTokens, from, websiteTokens]);

  const isStoreCardProduct = (product: Product) =>
    product.name === "digital_product" || product.name === "freebie";

  const getProductDisplayType = (product: Product): ProductDisplayType =>
    product.metadata?.product_details?.displayType === "full_view"
      ? "full_view"
      : "link";

  const getProductThumbnail = (product: Product) =>
    product.metadata?.product_details?.thumbnailUrl ||
    (product as any)?.product_thumbnail ||
    "";

  const handleStoreCardClick = (product: Product) => {
    if (from !== "frontend") return;
    openProductCheckoutInNewTab(product.id, { designTokens: cardTokens });
  };
  const fetchProducts = async () => {
    let shouldDelete = false;
    const loadStartedAt = Date.now();
    const MIN_SKELETON_MS = 280;

    try {
      // Only fetch the specific product that matches the component's product_id
      if (!component.post_ids) {
        setProducts([]);
        return;
      }

      const { data: product, error: productError } = await supabase
        .from("all_products")
        .select("*")
        .eq("id", component.post_ids)
        .neq("is_product_delete", true)
        .maybeSingle();

      if (productError) {
        throw productError;
      }

      if (!product) {
        setProducts([]);
        shouldDelete = true;
        return;
      }

      if (from === "frontend" && product.status !== "active") {
        setProducts([]);
        return;
      }

      const nextDisplayType =
        product.metadata?.product_details?.displayType === "full_view"
          ? "full_view"
          : "link";
      setLoaderDisplayType(nextDisplayType);
      try {
        if (component?.post_ids) {
          sessionStorage.setItem(
            `store-product-display-${component.post_ids}`,
            nextDisplayType
          );
        }
      } catch {
        /* ignore quota / private mode */
      }
      setProducts([product]);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      // Don't set loading to false if we're in the deletion process
      if (!shouldDelete) {
        const elapsed = Date.now() - loadStartedAt;
        const remaining = Math.max(0, MIN_SKELETON_MS - elapsed);
        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining));
        }
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setLoading(true); // Ensure loading state is set when fetching
    fetchProducts();
  }, [component, from]);

  // Add additional useEffect to refetch when switching to landing page
  useEffect(() => {
    if (from === "landing-page") {
      fetchProducts();
    }
  }, [from]);

  const handleSubmit = async (values: any, form: any, product: Product) => {
    try {
      if (!product.stripe_product_id || !product.stripe_price_id) {
        throw new Error("Product not properly configured in Stripe");
      }

      // Get custom fields from product metadata
      const customFields =
        product.metadata?.collect_info?.required_fields || [];

      // Create metadata object with standard fields
      const metadata: Record<string, any> = {
        id: product.id,
        customer_name: values.name,
        customer_email: values.email,
        product_name: product.metadata?.product_details?.title,
        product_category: product.category,
      };

      // Add custom field values to metadata
      customFields.forEach((field) => {
        if (field.label && values[field.label.toLowerCase()]) {
          // Convert field label to snake_case for consistency
          const metadataKey = `customer_${field.label
            .toLowerCase()
            .replace(/\s+/g, "_")}`;
          metadata[metadataKey] = values[field.label.toLowerCase()];
        }
      });

      // Create checkout session
      const response = await fetch(
        `${StaticVariables().API_BASE}/api/create-payment-intent`,
        {
          method: "POST",
          headers: getApiRequestHeaders(),
          body: JSON.stringify({
            items: [
              {
                id: product.id,
                quantity: 1,
              },
            ],
            email: values.email,
            userId: product.user_id,
            currency: "usd",
            payment_method_types: ["card"],
            metadata: metadata,
            testMode: isStripeTestModeSync(), // Add test mode flag
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create payment session");
      }

      if (data.success) {
        message.success("Payment successful");

        // try {
        //   const extendedProduct = product as unknown as ExtendedProduct;
        //   const delivery = extendedProduct.metadata?.delivery;

        //   if (!delivery) {
        //     setLoading(false);
        //     return;
        //   }

        //   if (delivery.type === 'redirect' && typeof delivery.content === 'string') {
        //     // Open redirect URL in new tab
        //     window.open(delivery.content, '_blank');
        //   } else if (delivery.type === 'file' && Array.isArray(delivery.content)) {
        //     // Download all files
        //     delivery.content.forEach(file => {
        //       const link = document.createElement('a');
        //       link.href = file.url;
        //       link.download = file.name;
        //       document.body.appendChild(link);
        //       link.click();
        //       document.body.removeChild(link);
        //     });
        //   }

        //   setLoading(false);
        // } catch (error) {
        //   console.error('Error handling delivery:', error);
        //   message.error('Failed to process your delivery. Please contact support.');
        //   setLoading(false);
        // }

        form.resetFields();
      } else {
        message.error("Payment failed");
      }
    } catch (error) {
      console.error("Payment error:", error);
      message.error("Failed to process payment. Please try again.");
    }
  };

  // If we're deleting, show nothing (component will be removed)
  if (isDeleting) {
    return null;
  }

  if (!products.length && !loading) {
    if (from !== "frontend") {
      return (
        <div className="mt-4">
          <NoPostMsg message="No Product Found" />
        </div>
      );
    }
    return null;
  }

  return (
    <div ref={surfaceRef}>
      {loading || isLoading ? (
        renderStoreCardLoader({
          displayType:
            products[0] != null
              ? getProductDisplayType(products[0])
              : loaderDisplayType,
          isMobileView,
          designTokens: mergedDesignTokens,
          // Public / preview: design-option colors. Dashboard editor: neutral gray.
          useDesignColors: from === "frontend" || from === "preview",
        })
      ) : (
        <div
          className={`${from === "frontend"
            ? "store-products-list"
            : "outter-section-highlight-subcomponent"
            }`}
          style={{
            borderRadius: "none",
            padding: 0,
            backgroundColor: designTokens?.primaryColor || "",
            boxShadow: "none",
            margin:"10px 0"
          }}
        >
          {products.length > 0 &&
            products.map((product) =>
              isStoreCardProduct(product) ? (
                <div
                  key={product.id}
                  className={`store-product-card-shell ${from === "frontend"
                    ? "frontend-product-wrapper"
                    : "dashboard-product-wrapper"
                    }`}
                >
                  <StoreProductCard
                    designTokens={cardTokens}
                    buttonTextColor={websiteTokens?.buttonTextColor}
                    title={
                      product.metadata?.product_details?.title || "Product title"
                    }
                    subtitle={product.metadata?.product_details?.subtitle}
                    thumbnailUrl={getProductThumbnail(product)}
                    buttonText={
                      product.metadata?.product_details?.buttonText ||
                      "Learn More"
                    }
                    displayType={getProductDisplayType(product)}
                    interactive={from === "frontend"}
                    isMobileView={isMobileView}
                    onClick={() => handleStoreCardClick(product)}
                    style={storeCardThemeStyle}
                  />
                </div>
              ) : (
                <div
                  key={product.id}
                  className={`store-product-wrapper ${from === "frontend"
                    ? "frontend-product-wrapper"
                    : "dashboard-product-wrapper"
                    }`}
                  style={{
                    backgroundColor: designTokens?.primaryColor
                      ? designTokens?.primaryColor
                      : "#fff",
                    borderColor: designTokens?.secondaryColor,
                  }}
                >
                  <div className="store-product-header">
                    <h2 className="store-product-title">
                      {product.metadata?.product_details?.title ||
                        "Product Title"}
                    </h2>
                  </div>

                  <div
                    className={`store-product-container ${from === "frontend"
                      ? "frontend-product-container"
                      : "dashboard-product-container"
                      }`}
                  >
                    <div className="store-product-thumbnail">
                      {product.metadata?.product_details?.thumbnailUrl && (
                        <img
                          src={product.metadata.product_details.thumbnailUrl}
                          alt={
                            product.metadata.product_details.title || "Product"
                          }
                          loading="lazy"
                          decoding="async"
                          className="product-image"
                        />
                      )}
                    </div>

                    <div className="store-product-content">
                      <ProductForm
                        product={product}
                        from={from}
                        onSubmit={(values, form, product) =>
                          handleSubmit(values, form, product)
                        }
                        brand_color={brand_color}
                        designTokens={designTokens}
                      />
                    </div>
                  </div>
                </div>
              )
            )}
        </div>
      )}
    </div>
  );
};

export default MyStoreProducts;
